import { STORAGE } from '../constants'
import type { Meta, StremioStream, Video } from '../types'

export interface PlaybackSession {
  stream: StremioStream
  meta: Meta
  video?: Video
  currentTime: number
  duration: number
  playing: boolean
  updatedAt: number
}

const MAX_SESSION_AGE = 12 * 60 * 60 * 1000

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function requiredText(value: unknown, maximum: number) {
  return typeof value === 'string' && value.length ? value.slice(0, maximum) : ''
}

function optionalText(value: unknown, maximum: number) {
  return typeof value === 'string' ? value.slice(0, maximum) : undefined
}

function safeHttpUrl(value: unknown, maximum = 4_096) {
  if (typeof value !== 'string' || !value || value.length > maximum) return undefined
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? value : undefined
  } catch { return undefined }
}

function stringList(value: unknown, maximumItems: number, maximumLength: number) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && Boolean(item))
      .slice(0, maximumItems).map((item) => item.slice(0, maximumLength))
    : undefined
}

function safeNumber(value: unknown, minimum: number, maximum: number) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(minimum, Math.min(maximum, value)) : undefined
}

function cleanVideo(value: unknown): Video | null {
  if (!record(value)) return null
  const id = requiredText(value.id, 240)
  if (!id) return null
  const chapters = Array.isArray(value.chapters) ? value.chapters.flatMap((chapter) => {
    if (!record(chapter)) return []
    const title = requiredText(chapter.title, 160)
    const start = safeNumber(chapter.start, 0, 86_400)
    return title && start !== undefined ? [{ title, start }] : []
  }).slice(0, 100) : undefined
  return {
    id,
    title: optionalText(value.title, 200),
    name: optionalText(value.name, 200),
    season: Number.isInteger(value.season) ? safeNumber(value.season, 0, 100_000) : undefined,
    episode: Number.isInteger(value.episode) ? safeNumber(value.episode, 0, 100_000) : undefined,
    released: optionalText(value.released, 80),
    thumbnail: safeHttpUrl(value.thumbnail, 2_000),
    overview: optionalText(value.overview, 800),
    chapters,
    introStart: safeNumber(value.introStart, 0, 86_400),
    introEnd: safeNumber(value.introEnd, 0, 86_400),
    creditsStart: safeNumber(value.creditsStart, 0, 86_400),
  }
}

function cleanMeta(value: unknown, currentVideoId = ''): Meta | null {
  if (!record(value) || (value.type !== 'movie' && value.type !== 'series')) return null
  const id = requiredText(value.id, 240)
  const name = requiredText(value.name, 200)
  if (!id || !name) return null
  const director = Array.isArray(value.director)
    ? stringList(value.director, 12, 100)
    : optionalText(value.director, 100)
  let videos: Video[] | undefined
  if (Array.isArray(value.videos)) {
    const clean = value.videos.map(cleanVideo).filter((entry): entry is Video => Boolean(entry))
    const currentIndex = currentVideoId ? clean.findIndex((entry) => entry.id === currentVideoId) : -1
    const start = currentIndex >= 0
      ? Math.max(0, Math.min(currentIndex - 45, Math.max(0, clean.length - 100)))
      : 0
    videos = clean.slice(start, start + 100)
  }
  const year = typeof value.year === 'number' && Number.isFinite(value.year)
    ? Math.max(0, Math.min(9999, value.year)) : optionalText(value.year, 20)
  return {
    id,
    type: value.type,
    name,
    imdb_id: optionalText(value.imdb_id, 240),
    poster: safeHttpUrl(value.poster, 2_000),
    background: safeHttpUrl(value.background, 2_000),
    logo: safeHttpUrl(value.logo, 2_000),
    description: optionalText(value.description, 1_200),
    releaseInfo: optionalText(value.releaseInfo, 80),
    year,
    imdbRating: optionalText(value.imdbRating, 20),
    runtime: optionalText(value.runtime, 40),
    genre: stringList(value.genre, 12, 40),
    genres: stringList(value.genres, 12, 40),
    released: optionalText(value.released, 80),
    addonUrl: safeHttpUrl(value.addonUrl, 500),
    cast: stringList(value.cast, 24, 100),
    director,
    videos,
    trailers: Array.isArray(value.trailers) ? value.trailers.flatMap((trailer) => {
      if (!record(trailer)) return []
      const source = requiredText(trailer.source, 500)
      return source ? [{ source, type: optionalText(trailer.type, 40) }] : []
    }).slice(0, 12) : undefined,
    trailerStreams: Array.isArray(value.trailerStreams) ? value.trailerStreams.flatMap((trailer) => {
      if (!record(trailer)) return []
      const ytId = requiredText(trailer.ytId, 120)
      return ytId ? [{ ytId, title: optionalText(trailer.title, 200) }] : []
    }).slice(0, 12) : undefined,
  }
}

function cleanHeaders(value: unknown) {
  if (!record(value)) return undefined
  const entries = Object.entries(value).flatMap(([key, header]) => (
    /^[!#$%&'*+.^_`|~0-9A-Za-z-]{1,80}$/.test(key) && typeof header === 'string'
      ? [[key, header.slice(0, 2_000)] as const] : []
  )).slice(0, 24)
  return entries.length ? Object.fromEntries(entries) : undefined
}

function cleanStream(value: unknown): StremioStream | null {
  if (!record(value)) return null
  const url = safeHttpUrl(value.url)
  const externalUrl = url ? undefined : safeHttpUrl(value.externalUrl)
  const infoHash = typeof value.infoHash === 'string' && /^[a-f0-9]{40}$/i.test(value.infoHash)
    ? value.infoHash.toLowerCase() : undefined
  if (!url && !infoHash) return null
  const behavior = record(value.behaviorHints) ? {
    bingeGroup: optionalText(value.behaviorHints.bingeGroup, 200),
    filename: optionalText(value.behaviorHints.filename, 500),
    videoSize: safeNumber(value.behaviorHints.videoSize, 0, 10_000_000_000_000),
    notWebReady: typeof value.behaviorHints.notWebReady === 'boolean' ? value.behaviorHints.notWebReady : undefined,
    proxyHeaders: record(value.behaviorHints.proxyHeaders) ? {
      request: cleanHeaders(value.behaviorHints.proxyHeaders.request),
      response: cleanHeaders(value.behaviorHints.proxyHeaders.response),
    } : undefined,
  } : undefined
  return {
    name: optionalText(value.name, 300),
    title: optionalText(value.title, 1_200),
    description: optionalText(value.description, 1_200),
    url,
    externalUrl,
    ytId: optionalText(value.ytId, 120),
    infoHash,
    fileIdx: Number.isInteger(value.fileIdx) ? safeNumber(value.fileIdx, 0, 100_000) : undefined,
    sources: stringList(value.sources, 64, 500),
    fileMustInclude: stringList(value.fileMustInclude, 20, 200),
    addonUrl: safeHttpUrl(value.addonUrl, 500),
    behaviorHints: behavior,
  }
}

function cleanSession(value: unknown, enforceAge: boolean): PlaybackSession | null {
  if (!record(value)) return null
  const stream = cleanStream(value.stream)
  const video = value.video === undefined ? undefined : cleanVideo(value.video)
  const meta = cleanMeta(value.meta, video?.id)
  const currentTime = safeNumber(value.currentTime, 0, 86_400)
  const duration = safeNumber(value.duration, 0, 86_400)
  const updatedAt = safeNumber(value.updatedAt, 0, 9_999_999_999_999)
  if (!stream || !meta || (value.video !== undefined && !video) || currentTime === undefined
    || duration === undefined || updatedAt === undefined || typeof value.playing !== 'boolean') return null
  const age = Date.now() - updatedAt
  if (enforceAge && (age > MAX_SESSION_AGE || age < -5 * 60_000)) return null
  return { stream, meta, video: video || undefined, currentTime, duration, playing: value.playing, updatedAt }
}

export function savePlaybackSession(value: PlaybackSession) {
  try {
    const clean = cleanSession({ ...value, updatedAt: Date.now() }, false)
    if (!clean) {
      localStorage.removeItem(STORAGE.playbackSession)
      return
    }
    localStorage.setItem(STORAGE.playbackSession, JSON.stringify(clean))
  } catch { /* storage quota must never interrupt playback */ }
}

export function getPlaybackSession(): PlaybackSession | null {
  try {
    return cleanSession(JSON.parse(localStorage.getItem(STORAGE.playbackSession) || 'null') as unknown, true)
  } catch { return null }
}

export function clearPlaybackSession() {
  localStorage.removeItem(STORAGE.playbackSession)
}
